import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import AccountService from '../services/account.service';
import Button from '../components/DesignSystem/button';
import PdfViewer from '../components/PdfViewer';
import { MY_SELLER_BUCKET_PRE_URL, TERMS_DEFAULT_FILE } from '../utils/apiConstants';

interface RequestDetails {
  id: number;
  status: number;
  dateCreated: string;
}

const PrivateNodeRequestStatus: React.FC = () => {
  const [requests, setRequests] = useState<RequestDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [currentId, setCurrentId] = useState<number>(1);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await AccountService.getPrivateNodeRequestStatus();
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching request status:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const getStatusText = (status: number): string => {
    switch (status) {
      case 1:
        return 'Request Submitted';
      case 2:
        return 'Awaiting Signature';
      case 3:
        return 'Signature Submitted';
      case 4:
        return 'In Delivery';
      case 5:
        return 'Confirm Installation Schedule';
      case 6:
        return 'Verify Installation';
      case 7:
        return 'Complete';
      default:
        return 'Unknown Status';
    }
  };

  const getStatusClassName = (status: number): string => {
    switch (status) {
      case 7:
        return 'completed';
      case 1:
        return 'submitted';
      case 2:
        return 'packing';
      case 3:
        return 'delivery';
      default:
        return 'unknown';
    }
  };

  const getPdfUrl = (status: number): string => {
    switch (status) {
      case 2:
        return MY_SELLER_BUCKET_PRE_URL + TERMS_DEFAULT_FILE;  
      case 6:
        return MY_SELLER_BUCKET_PRE_URL + TERMS_DEFAULT_FILE; 
      default:
        return '';
    }
  };

  const handlePdfClose = (signed = false) => {
    setPdfViewerOpen(false);
    if (signed) {
      AccountService.updatePrivateNodeRequestStatus(currentId)
        .then(() => {
          fetchRequests();
        })
        .catch((error) => {
          console.error('Error updating request status: ', error)
        })
    }
  }

  const getActionNeeded = (request: RequestDetails): JSX.Element => {
    switch (request.status) {
      case 1:
      case 3:
        return <span>No action needed</span>;
      case 2:
      case 6:
        return (
          <Button 
            variant="primary" 
            className="btn-terms-of-service" 
            node={request.status === 2 ? "Sign Agreement" : "Verify Installation"} 
            onClick={() => {
              setPdfUrl(getPdfUrl(request.status));
              setCurrentId(request.id)
              setPdfViewerOpen(true);
            }}
          />
        );
      case 4:
        return <span>Schedule installation date</span>;
      case 7:
        return <span>Request completed</span>;
      default:
        return <span>Unknown</span>;
    }
  };

  if (loading) {
    return (
      <div className="pnr-status-root">
        <div className="loading-state">Loading requests...</div>
      </div>
    );
  }

  return (
    <div className="pnr-status-root">
      <div className="title-main">
        <div className="title-root">
          <h3>Private Node Requests</h3>
        </div>
      </div>
      
      <div className="status-table">
        <div className="d-table">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow className="table-head-row">
                  <TableCell>Request ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Action Needed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>#{request.id}</TableCell>
                      <TableCell>
                        <div className={`status-badge ${getStatusClassName(request.status)}`}>
                          {getStatusText(request.status)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {new Date(request.dateCreated).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{getActionNeeded(request)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
      <PdfViewer open={pdfViewerOpen} onClose={handlePdfClose} url={pdfUrl}/>
    </div>
  );
};

export default PrivateNodeRequestStatus;